import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  BarChart3,
  Clipboard,
  Coins,
  CreditCard,
  Download,
  Factory,
  FileSpreadsheet,
  Loader2,
  ReceiptText,
  RefreshCw,
  Search,
  TrendingUp,
  Users,
  WalletCards,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { adminBillingApi, type AdminRedemptionCode, type RedemptionCodePurpose } from '@/api/adminBillingApi'
import { adminUsageApi, type AdminUsageDailyRow, type AdminUsageFilters, type AdminUsageRow, type AdminUsageTotal } from '@/api/adminUsageApi'
import { listAdminUsers, type AdminUser } from '@/api/adminUsersApi'
import type { UsageServiceKind } from '@/api/creditsApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { exportCsv, type CsvHeader } from '@/lib/exportCsv'
import { SERVICE_COLORS, SERVICE_KINDS } from '@/lib/serviceColors'
import { formatCount, formatCredits, formatCurrency, formatDateTime, formatNumber, formatPercent } from '@/utils/format'

type ServiceFilter = UsageServiceKind | 'all'
type ExportRow = Record<string, string | number>

const CREDIT_USD_RATE = 0.01
const USER_LIMIT = 100
const EMPTY_TOTAL: AdminUsageTotal = {
  quantity: 0,
  input_tokens: 0,
  output_tokens: 0,
  cached_input_tokens: 0,
  has_missing_token_split: false,
  cost_usd: 0,
  credits: 0,
  margin_usd: 0,
  count: 0,
  internal_cost_usd: 0,
  internal_credits: 0,
}
const SERVICE_FILTERS: ServiceFilter[] = ['all', ...SERVICE_KINDS]
const CODE_PURPOSES: RedemptionCodePurpose[] = ['sale', 'marketing', 'test', 'comp']

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function createIdempotencyKey(): string {
  return `admin-topup-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function serviceLabelKey(kind: ServiceFilter | ''): string {
  return kind === 'all' || kind === '' ? 'adminConsole.billing.filters.allServices' : `billing.usage.services.${kind}`
}

function revenueUSD(credits: number): number {
  return credits * CREDIT_USD_RATE
}

function marginRate(total: Pick<AdminUsageTotal, 'credits' | 'margin_usd'>): number {
  const revenue = revenueUSD(total.credits)
  return revenue > 0 ? total.margin_usd / revenue : 0
}

function userName(users: AdminUser[], userId?: string): string {
  if (!userId) return ''
  const user = users.find((item) => item.user_id === userId)
  return user?.display_name || user?.username || userId
}

function buildCsvName(prefix: string, month: string): string {
  return `${prefix}-${month || today()}.csv`
}

function formatLedgerType(type: string, t: (key: string) => string): string {
  const key = `adminConsole.billing.ledgerTypes.${type}`
  const translated = t(key)
  return translated === key ? type : translated
}

function asFiniteNumber(value: string): number {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function copyText(text: string, successMessage: string) {
  const write = navigator.clipboard?.writeText(text)
  if (write) {
    void write.then(() => toast.success(successMessage)).catch(() => undefined)
  }
}

function KpiCard({ icon: Icon, label, value, detail }: { icon: typeof Activity; label: string; value: string; detail?: string }) {
  return (
    <Card className="rounded-lg">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        <Icon className="size-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="nf-mono text-2xl font-semibold">{value}</div>
        {detail ? <div className="mt-1 text-xs text-muted-foreground">{detail}</div> : null}
      </CardContent>
    </Card>
  )
}

function EmptyState({ label }: { label: string }) {
  return <div className="flex min-h-32 items-center justify-center text-sm text-muted-foreground">{label}</div>
}

function LoadingBlock({ className = 'h-44' }: { className?: string }) {
  return <Skeleton className={`${className} w-full`} />
}

function TokenSplitDetails({ row, t, className = '' }: { row: AdminUsageRow; t: (key: string) => string; className?: string }) {
  if (row.unit_type !== 'token') return null
  return (
    <div className={`flex flex-wrap gap-x-2 text-xs text-muted-foreground ${className}`}>
      <span className="nf-mono">
        {t('billing.usage.inputTokens')} {formatCount(row.input_tokens ?? 0)}
        {' / '}
        {t('billing.usage.outputTokens')} {formatCount(row.output_tokens ?? 0)}
        {' / '}
        {t('billing.usage.cachedInputTokens')} {formatCount(row.cached_input_tokens ?? 0)}
      </span>
      {row.has_missing_token_split ? <span>{t('billing.usage.partialTokenSplit')}</span> : null}
    </div>
  )
}

function TrendChart({ rows }: { rows: AdminUsageDailyRow[] }) {
  const maxValue = rows.reduce((max, row) => Math.max(max, row.cost_usd, revenueUSD(row.credits), row.margin_usd), 0)
  const points = (selector: (row: AdminUsageDailyRow) => number) =>
    rows
      .map((row, index) => {
        const x = rows.length === 1 ? 50 : (index / (rows.length - 1)) * 100
        const value = selector(row)
        const y = maxValue > 0 ? 92 - (Math.max(value, 0) / maxValue) * 84 : 92
        return `${x.toFixed(2)},${y.toFixed(2)}`
      })
      .join(' ')

  return (
    <div className="h-64">
      <svg className="h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-hidden>
        {[25, 50, 75].map((y) => (
          <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="currentColor" strokeOpacity="0.09" strokeWidth="0.4" vectorEffect="non-scaling-stroke" />
        ))}
        <polyline points={points((row) => row.cost_usd)} fill="none" stroke="#60a5fa" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <polyline points={points((row) => revenueUSD(row.credits))} fill="none" stroke="#34d399" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <polyline points={points((row) => row.margin_usd)} fill="none" stroke="#f59e0b" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  )
}

function ServiceShare({ rows, t }: { rows: AdminUsageRow[]; t: (key: string) => string }) {
  const totalCredits = rows.reduce((sum, row) => sum + row.credits, 0)
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
      <div className="space-y-3">
        {SERVICE_KINDS.map((kind) => {
          const row = rows.find((item) => item.service_kind === kind)
          const credits = row?.credits ?? 0
          const width = totalCredits > 0 ? Math.max((credits / totalCredits) * 100, credits > 0 ? 3 : 0) : 0
          return (
            <div key={kind} className="space-y-1">
              <div className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-center gap-2 text-sm">
                <span className="flex items-center gap-1.5 truncate text-muted-foreground">
                  <span className="inline-block size-2.5 rounded-full" style={{ backgroundColor: SERVICE_COLORS[kind] }} />
                  {t(serviceLabelKey(kind))}
                </span>
                <div className="h-6 overflow-hidden rounded-md bg-muted">
                  <div className="h-full rounded-md" style={{ width: `${width}%`, backgroundColor: SERVICE_COLORS[kind] }} />
                </div>
                <span className="nf-mono text-right">{formatCredits(credits)}</span>
              </div>
              {row ? <TokenSplitDetails row={row} t={t} className="ml-[6.5rem]" /> : null}
            </div>
          )
        })}
      </div>
      <svg className="h-44 w-full text-primary" viewBox="0 0 120 120" role="img" aria-label={t('adminConsole.billing.serviceShare')}>
        <circle cx="60" cy="60" r="42" fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="18" />
        {SERVICE_KINDS.map((kind, index) => {
          const credits = rows.find((item) => item.service_kind === kind)?.credits ?? 0
          const share = totalCredits > 0 ? credits / totalCredits : 0
          const previous = SERVICE_KINDS.slice(0, index).reduce((sum, prev) => sum + ((rows.find((item) => item.service_kind === prev)?.credits ?? 0) / (totalCredits || 1)), 0)
          const circumference = 2 * Math.PI * 42
          return (
            <circle
              key={kind}
              cx="60"
              cy="60"
              r="42"
              fill="none"
              stroke={SERVICE_COLORS[kind]}
              strokeWidth="18"
              strokeDasharray={`${share * circumference} ${circumference}`}
              strokeDashoffset={-(previous * circumference)}
              transform="rotate(-90 60 60)"
            />
          )
        })}
        <text x="60" y="56" textAnchor="middle" className="fill-current text-[13px] font-semibold">
          {formatCredits(totalCredits)}
        </text>
        <text x="60" y="74" textAnchor="middle" className="fill-muted-foreground text-[9px]">
          {t('adminConsole.billing.credits')}
        </text>
      </svg>
    </div>
  )
}

export default function AdminUsageDashboard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [month, setMonth] = useState(currentMonth)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [serviceKind, setServiceKind] = useState<ServiceFilter>('all')
  const [includeInternal, setIncludeInternal] = useState(false)
  const [model, setModel] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [topUpAmount, setTopUpAmount] = useState('')
  const [topUpNote, setTopUpNote] = useState('')
  const [codeCount, setCodeCount] = useState('1')
  const [codeAmount, setCodeAmount] = useState('')
  const [codePurpose, setCodePurpose] = useState<RedemptionCodePurpose>('sale')
  const [codeExpiresAt, setCodeExpiresAt] = useState('')
  const [codeNote, setCodeNote] = useState('')
  const [generatedCodes, setGeneratedCodes] = useState<AdminRedemptionCode[]>([])

  const filters = useMemo<AdminUsageFilters>(() => ({
    month: month.trim(),
    start: start.trim(),
    end: end.trim(),
    service_kind: serviceKind,
    model: model.trim(),
    user_id: selectedUserId.trim(),
    project_id: projectId.trim(),
    ...(includeInternal ? { include_internal: true } : {}),
  }), [end, includeInternal, model, month, projectId, selectedUserId, serviceKind, start])

  const selectedUserFilters = useMemo<AdminUsageFilters>(() => ({ ...filters, user_id: selectedUserId.trim() }), [filters, selectedUserId])

  const usersQuery = useQuery({
    queryKey: ['admin', 'billing', 'users', userSearch],
    queryFn: () => listAdminUsers({ limit: USER_LIMIT, offset: 0, q: userSearch }),
  })
  const summaryQuery = useQuery({
    queryKey: ['admin', 'billing', 'summary', filters],
    queryFn: () => adminUsageApi.get(filters),
  })
  const byServiceQuery = useQuery({
    queryKey: ['admin', 'billing', 'by-service', filters],
    queryFn: () => adminUsageApi.byService(filters),
  })
  const byModelQuery = useQuery({
    queryKey: ['admin', 'billing', 'by-model', filters],
    queryFn: () => adminUsageApi.byModel(filters),
  })
  const dailyQuery = useQuery({
    queryKey: ['admin', 'billing', 'daily', filters],
    queryFn: () => adminUsageApi.daily(filters),
  })
  const selectedUserUsageQuery = useQuery({
    queryKey: ['admin', 'billing', 'selected-user-usage', selectedUserFilters],
    queryFn: () => adminUsageApi.get(selectedUserFilters),
    enabled: Boolean(selectedUserId),
  })
  const selectedUserServiceQuery = useQuery({
    queryKey: ['admin', 'billing', 'selected-user-service', selectedUserFilters],
    queryFn: () => adminUsageApi.byService(selectedUserFilters),
    enabled: Boolean(selectedUserId),
  })
  const selectedUserModelQuery = useQuery({
    queryKey: ['admin', 'billing', 'selected-user-model', selectedUserFilters],
    queryFn: () => adminUsageApi.byModel(selectedUserFilters),
    enabled: Boolean(selectedUserId),
  })
  const balanceQuery = useQuery({
    queryKey: ['admin', 'billing', 'balance', selectedUserId],
    queryFn: () => adminBillingApi.balance(selectedUserId),
    enabled: Boolean(selectedUserId),
  })
  const ledgerQuery = useQuery({
    queryKey: ['admin', 'billing', 'ledger', selectedUserId],
    queryFn: () => adminBillingApi.ledger({ user_id: selectedUserId, limit: 50, offset: 0 }),
    enabled: Boolean(selectedUserId),
  })

  const users = usersQuery.data?.users ?? []
  const summaryRows = summaryQuery.data?.rows ?? []
  const summaryTotal = summaryQuery.data?.total ?? EMPTY_TOTAL
  const serviceRows = byServiceQuery.data?.rows ?? []
  const dailyRows = dailyQuery.data?.rows ?? []
  const selectedUserRows = selectedUserUsageQuery.data?.rows ?? []
  const selectedUserTotal = selectedUserUsageQuery.data?.total ?? EMPTY_TOTAL
  const selectedUserServiceRows = selectedUserServiceQuery.data?.rows ?? []
  const selectedUserModelRows = selectedUserModelQuery.data?.rows ?? []
  const ledgerRows = ledgerQuery.data?.transactions ?? []

  const userRankRows = useMemo(() => {
    const map = new Map<string, AdminUsageTotal>()
    for (const row of summaryRows) {
      const userId = row.user_id || t('adminConsole.billing.unknown')
      const current = map.get(userId) ?? { ...EMPTY_TOTAL }
      current.quantity += row.quantity
      current.cost_usd += row.cost_usd
      current.credits += row.credits
      current.margin_usd += row.margin_usd
      current.count += row.count
      map.set(userId, current)
    }
    return Array.from(map.entries())
      .map(([user_id, total]) => ({ user_id, user_name: userName(users, user_id), ...total }))
      .sort((a, b) => b.credits - a.credits)
      .slice(0, 10)
  }, [summaryRows, t, users])

  const projectRows = useMemo(() => {
    const map = new Map<string, AdminUsageTotal>()
    for (const row of selectedUserRows) {
      const id = row.project_id || t('adminConsole.billing.unknown')
      const current = map.get(id) ?? { ...EMPTY_TOTAL }
      current.quantity += row.quantity
      current.cost_usd += row.cost_usd
      current.credits += row.credits
      current.margin_usd += row.margin_usd
      current.count += row.count
      map.set(id, current)
    }
    return Array.from(map.entries()).map(([project_id, total]) => ({ project_id, ...total })).sort((a, b) => b.credits - a.credits)
  }, [selectedUserRows, t])

  // 项目筛选可发现性: 从当前 summary 行派生出现过的 project_id 作 datalist 建议。
  // 字段为空(=全部项目)时 summaryRows 含全部项目, 此时建议最全, 正是要选项目的时机。
  // 保留自由文本输入(admin 跨用户无项目名, 全局过滤器, 空=全部), 不强套 ProjectCombobox。
  const projectIdOptions = useMemo(() => {
    const ids = new Set<string>()
    for (const row of summaryRows) {
      if (row.project_id) ids.add(row.project_id)
    }
    return Array.from(ids).sort()
  }, [summaryRows])

  const financeByServiceRows = useMemo(() => serviceRows.map((row) => ({
    ...row,
    revenue_usd: revenueUSD(row.credits),
    margin_rate: marginRate(row),
  })), [serviceRows])

  const isLoading = summaryQuery.isLoading || byServiceQuery.isLoading || byModelQuery.isLoading || dailyQuery.isLoading

  const refetchAll = () => {
    void summaryQuery.refetch()
    void byServiceQuery.refetch()
    void byModelQuery.refetch()
    void dailyQuery.refetch()
    void selectedUserUsageQuery.refetch()
    void selectedUserServiceQuery.refetch()
    void selectedUserModelQuery.refetch()
    void balanceQuery.refetch()
    void ledgerQuery.refetch()
  }

  const topUpMutation = useMutation({
    mutationFn: () => adminBillingApi.topUp({
      user_id: selectedUserId.trim(),
      amount: asFiniteNumber(topUpAmount),
      idempotency_key: createIdempotencyKey(),
      ...(topUpNote.trim() ? { note: topUpNote.trim() } : {}),
    }),
    onSuccess: (balance) => {
      toast.success(t('adminConsole.billing.wallet.topUpSuccess', { balance: formatCredits(balance.balance) }))
      setTopUpAmount('')
      setTopUpNote('')
      void queryClient.invalidateQueries({ queryKey: ['admin', 'billing'] })
    },
    onError: (error) => toast.error(friendlyMessage(error, t('common.serviceError'))),
  })

  const codesMutation = useMutation({
    mutationFn: () => {
      const expires = codeExpiresAt ? Math.floor(new Date(`${codeExpiresAt}T00:00:00Z`).getTime() / 1000) : undefined
      return adminBillingApi.generateCodes({
        count: Math.max(1, Math.floor(asFiniteNumber(codeCount))),
        amount: Math.max(1, Math.floor(asFiniteNumber(codeAmount))),
        purpose: codePurpose,
        ...(expires ? { expires_at: expires } : {}),
        ...(codeNote.trim() ? { note: codeNote.trim() } : {}),
      })
    },
    onSuccess: (response) => {
      setGeneratedCodes(response.codes)
      toast.success(t('adminConsole.billing.wallet.codesSuccess', { count: response.codes.length }))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('common.serviceError'))),
  })

  const exportUsageRows = () => {
    const headers: Array<CsvHeader<ExportRow>> = [
      { key: 'user_id', label: t('adminConsole.billing.csv.userId') },
      { key: 'user_name', label: t('adminConsole.billing.csv.userName') },
      { key: 'project_id', label: t('adminConsole.billing.csv.projectId') },
      { key: 'service_kind', label: t('adminConsole.billing.csv.service') },
      { key: 'model_alias', label: t('adminConsole.billing.csv.model') },
      { key: 'unit_type', label: t('adminConsole.billing.csv.unit') },
      { key: 'quantity', label: t('adminConsole.billing.csv.quantity') },
      { key: 'unit_price_usd', label: t('adminConsole.billing.csv.unitCost') },
      { key: 'cost_usd', label: t('adminConsole.billing.csv.cost') },
      { key: 'credits', label: t('adminConsole.billing.csv.credits') },
      { key: 'revenue_usd', label: t('adminConsole.billing.csv.revenue') },
      { key: 'margin_usd', label: t('adminConsole.billing.csv.margin') },
      { key: 'count', label: t('adminConsole.billing.csv.count') },
    ]
    exportCsv(buildCsvName(t('adminConsole.billing.files.usage'), month), headers, summaryRows.map((row) => ({
      user_id: row.user_id ?? '',
      user_name: userName(users, row.user_id),
      project_id: row.project_id ?? '',
      service_kind: row.service_kind ? t(serviceLabelKey(row.service_kind)) : '',
      model_alias: row.model_alias ?? '',
      unit_type: row.unit_type ?? '',
      quantity: row.quantity,
      unit_price_usd: row.unit_price_usd ?? '',
      cost_usd: row.cost_usd,
      credits: row.credits,
      revenue_usd: revenueUSD(row.credits).toFixed(2),
      margin_usd: row.margin_usd,
      count: row.count,
    })))
  }

  const exportDailyRows = () => {
    const headers: Array<CsvHeader<ExportRow>> = [
      { key: 'date', label: t('adminConsole.billing.csv.date') },
      { key: 'quantity', label: t('adminConsole.billing.csv.quantity') },
      { key: 'cost_usd', label: t('adminConsole.billing.csv.cost') },
      { key: 'credits', label: t('adminConsole.billing.csv.credits') },
      { key: 'revenue_usd', label: t('adminConsole.billing.csv.revenue') },
      { key: 'margin_usd', label: t('adminConsole.billing.csv.margin') },
      { key: 'margin_rate', label: t('adminConsole.billing.csv.marginRate') },
      { key: 'count', label: t('adminConsole.billing.csv.count') },
    ]
    exportCsv(buildCsvName(t('adminConsole.billing.files.daily'), month), headers, dailyRows.map((row) => ({
      date: row.date,
      quantity: row.quantity,
      cost_usd: row.cost_usd,
      credits: row.credits,
      revenue_usd: revenueUSD(row.credits).toFixed(2),
      margin_usd: row.margin_usd,
      margin_rate: formatPercent(marginRate(row), 2),
      count: row.count,
    })))
  }

  const exportLedgerRows = () => {
    const headers: Array<CsvHeader<ExportRow>> = [
      { key: 'id', label: t('adminConsole.billing.csv.ledgerId') },
      { key: 'time', label: t('adminConsole.billing.csv.time') },
      { key: 'user_id', label: t('adminConsole.billing.csv.userId') },
      { key: 'type', label: t('adminConsole.billing.csv.type') },
      { key: 'amount', label: t('adminConsole.billing.csv.creditDelta') },
      { key: 'balance_after', label: t('adminConsole.billing.csv.balanceAfter') },
      { key: 'ref_job_id', label: t('adminConsole.billing.csv.refJob') },
      { key: 'note', label: t('adminConsole.billing.csv.note') },
    ]
    exportCsv(buildCsvName(t('adminConsole.billing.files.ledger'), month), headers, ledgerRows.map((row) => ({
      id: Number(row.id ?? 0),
      time: formatDateTime(row.created_at),
      user_id: selectedUserId,
      type: formatLedgerType(row.type, t),
      amount: row.amount,
      balance_after: row.balance_after,
      ref_job_id: String(row.ref_job_id ?? ''),
      note: String(row.note ?? ''),
    })))
  }

  const exportUserStatement = () => {
    const headers: Array<CsvHeader<ExportRow>> = [
      { key: 'user_id', label: t('adminConsole.billing.csv.userId') },
      { key: 'user_name', label: t('adminConsole.billing.csv.userName') },
      { key: 'period', label: t('adminConsole.billing.csv.period') },
      { key: 'service_kind', label: t('adminConsole.billing.csv.service') },
      { key: 'model_alias', label: t('adminConsole.billing.csv.model') },
      { key: 'quantity', label: t('adminConsole.billing.csv.quantity') },
      { key: 'credits', label: t('adminConsole.billing.csv.credits') },
      { key: 'cost_usd', label: t('adminConsole.billing.csv.cost') },
      { key: 'revenue_usd', label: t('adminConsole.billing.csv.revenue') },
      { key: 'margin_usd', label: t('adminConsole.billing.csv.margin') },
      { key: 'balance', label: t('adminConsole.billing.csv.balance') },
      { key: 'available', label: t('adminConsole.billing.csv.available') },
      { key: 'reserved', label: t('adminConsole.billing.csv.reserved') },
    ]
    exportCsv(buildCsvName(t('adminConsole.billing.files.statement'), month), headers, selectedUserRows.map((row) => ({
      user_id: selectedUserId,
      user_name: userName(users, selectedUserId),
      period: month,
      service_kind: row.service_kind ? t(serviceLabelKey(row.service_kind)) : '',
      model_alias: row.model_alias ?? '',
      quantity: row.quantity,
      credits: row.credits,
      cost_usd: row.cost_usd,
      revenue_usd: revenueUSD(row.credits).toFixed(2),
      margin_usd: row.margin_usd,
      balance: balanceQuery.data?.balance ?? '',
      available: balanceQuery.data?.available ?? '',
      reserved: balanceQuery.data?.reserved ?? '',
    })))
  }

  return (
    <div className="space-y-4">
      <Card className="rounded-lg">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle>{t('adminConsole.billing.title')}</CardTitle>
              <CardDescription>{t('adminConsole.billing.description')}</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={refetchAll}>
              <RefreshCw className="size-4" />
              {t('common.retry')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="admin-billing-month">{t('adminConsole.billing.filters.month')}</Label>
              <Input id="admin-billing-month" value={month} onChange={(event) => setMonth(event.target.value)} placeholder="2026-06" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-billing-start">{t('adminConsole.billing.filters.start')}</Label>
              <Input id="admin-billing-start" value={start} onChange={(event) => setStart(event.target.value)} placeholder="2026-06-01" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-billing-end">{t('adminConsole.billing.filters.end')}</Label>
              <Input id="admin-billing-end" value={end} onChange={(event) => setEnd(event.target.value)} placeholder="2026-06-30" />
            </div>
            <div className="space-y-2">
              <Label>{t('adminConsole.billing.filters.service')}</Label>
              <Select value={serviceKind} onValueChange={(value) => setServiceKind(value as ServiceFilter)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_FILTERS.map((kind) => <SelectItem key={kind} value={kind}>{t(serviceLabelKey(kind))}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-billing-model">{t('adminConsole.billing.filters.model')}</Label>
              <Input id="admin-billing-model" value={model} onChange={(event) => setModel(event.target.value)} placeholder={t('adminConsole.billing.filters.modelPlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-billing-user">{t('adminConsole.billing.filters.user')}</Label>
              <Input id="admin-billing-user" value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)} placeholder={t('adminConsole.billing.filters.userPlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-billing-project">{t('adminConsole.billing.filters.project')}</Label>
              <Input id="admin-billing-project" list="admin-billing-project-options" value={projectId} onChange={(event) => setProjectId(event.target.value)} placeholder={t('adminConsole.billing.filters.projectPlaceholder')} />
              <datalist id="admin-billing-project-options">
                {projectIdOptions.map((id) => <option key={id} value={id} />)}
              </datalist>
            </div>
          </div>
          <div className="flex items-center gap-2 border-t border-border/60 pt-3">
            <Switch id="admin-billing-include-internal" checked={includeInternal} onCheckedChange={setIncludeInternal} />
            <Label htmlFor="admin-billing-include-internal" className="cursor-pointer text-sm">{t('adminConsole.billing.filters.includeInternal')}</Label>
            <span className="ml-2 text-xs text-muted-foreground">{t('adminConsole.billing.filters.includeInternalHint')}</span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex h-auto w-full flex-wrap justify-start">
          <TabsTrigger value="overview"><BarChart3 className="size-4" />{t('adminConsole.billing.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="users"><Users className="size-4" />{t('adminConsole.billing.tabs.users')}</TabsTrigger>
          <TabsTrigger value="details"><Factory className="size-4" />{t('adminConsole.billing.tabs.details')}</TabsTrigger>
          <TabsTrigger value="finance"><FileSpreadsheet className="size-4" />{t('adminConsole.billing.tabs.finance')}</TabsTrigger>
          <TabsTrigger value="wallet"><WalletCards className="size-4" />{t('adminConsole.billing.tabs.wallet')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            {isLoading ? (
              Array.from({ length: 6 }, (_, index) => <LoadingBlock key={index} className="h-28" />)
            ) : (
              <>
                <KpiCard icon={Activity} label={t('adminConsole.billing.metrics.quantity')} value={formatCount(summaryTotal.quantity)} detail={t('adminConsole.billing.metrics.events', { count: formatCount(summaryTotal.count) })} />
                <KpiCard icon={CreditCard} label={t('adminConsole.billing.metrics.cost')} value={formatCurrency(summaryTotal.cost_usd)} detail={includeInternal ? t('adminConsole.billing.scope.allIncluded') : t('adminConsole.billing.scope.customer')} />
                <KpiCard icon={Coins} label={t('adminConsole.billing.metrics.credits')} value={formatCredits(summaryTotal.credits)} detail={`${formatCurrency(revenueUSD(summaryTotal.credits))} · ${includeInternal ? t('adminConsole.billing.scope.allIncluded') : t('adminConsole.billing.scope.customer')}`} />
                <KpiCard icon={TrendingUp} label={t('adminConsole.billing.metrics.margin')} value={formatCurrency(summaryTotal.margin_usd)} detail={includeInternal ? t('adminConsole.billing.scope.allIncluded') : t('adminConsole.billing.scope.customer')} />
                <KpiCard icon={ReceiptText} label={t('adminConsole.billing.metrics.marginRate')} value={formatPercent(marginRate(summaryTotal), 2)} detail={includeInternal ? t('adminConsole.billing.scope.includesInternalHint') : t('adminConsole.billing.scope.customerHint')} />
                <KpiCard icon={Factory} label={t('adminConsole.billing.metrics.internalCost')} value={formatCurrency(summaryTotal.internal_cost_usd)} detail={t('adminConsole.billing.metrics.internalCredits', { credits: formatCredits(summaryTotal.internal_credits) })} />
              </>
            )}
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle>{t('adminConsole.billing.trendTitle')}</CardTitle>
                <CardDescription>{t('adminConsole.billing.trendDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {dailyQuery.isLoading ? <LoadingBlock className="h-64" /> : dailyRows.length ? (
                  <div className="space-y-3">
                    <TrendChart rows={dailyRows} />
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-[#60a5fa]" />{t('adminConsole.billing.metrics.cost')}</span>
                      <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-[#34d399]" />{t('adminConsole.billing.metrics.revenue')}</span>
                      <span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-full bg-[#f59e0b]" />{t('adminConsole.billing.metrics.margin')}</span>
                    </div>
                  </div>
                ) : <EmptyState label={t('adminConsole.billing.empty')} />}
              </CardContent>
            </Card>

            <Card className="rounded-lg">
              <CardHeader>
                <CardTitle>{t('adminConsole.billing.serviceShare')}</CardTitle>
              </CardHeader>
              <CardContent>
                {byServiceQuery.isLoading ? <LoadingBlock /> : serviceRows.length ? <ServiceShare rows={serviceRows} t={t} /> : <EmptyState label={t('adminConsole.billing.empty')} />}
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle>{t('adminConsole.billing.topUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              {summaryQuery.isLoading ? <LoadingBlock /> : userRankRows.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('adminConsole.billing.columns.user')}</TableHead>
                      <TableHead className="text-right">{t('adminConsole.billing.metrics.credits')}</TableHead>
                      <TableHead className="text-right">{t('adminConsole.billing.metrics.cost')}</TableHead>
                      <TableHead className="text-right">{t('adminConsole.billing.metrics.margin')}</TableHead>
                      <TableHead className="text-right">{t('adminConsole.billing.metrics.marginRate')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userRankRows.map((row) => (
                      <TableRow key={row.user_id} className="cursor-pointer" onClick={() => setSelectedUserId(row.user_id)}>
                        <TableCell>
                          <div className="font-medium">{row.user_name}</div>
                          <div className="nf-mono text-xs text-muted-foreground">{row.user_id}</div>
                        </TableCell>
                        <TableCell className="nf-mono text-right">{formatCredits(row.credits)}</TableCell>
                        <TableCell className="nf-mono text-right">{formatCurrency(row.cost_usd)}</TableCell>
                        <TableCell className="nf-mono text-right">{formatCurrency(row.margin_usd)}</TableCell>
                        <TableCell className="nf-mono text-right">{formatPercent(marginRate(row), 2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <EmptyState label={t('adminConsole.billing.empty')} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card className="rounded-lg">
            <CardHeader className="gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle>{t('adminConsole.billing.userAnalysis')}</CardTitle>
                  <CardDescription>{t('adminConsole.billing.userAnalysisDescription')}</CardDescription>
                </div>
                <Button type="button" variant="outline" onClick={exportUserStatement} disabled={!selectedUserId || !selectedUserRows.length}>
                  <Download className="size-4" />
                  {t('adminConsole.billing.exportCsv')}
                </Button>
              </div>
              <div className="flex flex-col gap-2 md:flex-row">
                <Input value={userSearch} onChange={(event) => setUserSearch(event.target.value)} placeholder={t('adminConsole.billing.userSearchPlaceholder')} />
                <Button type="button" variant="outline" onClick={() => void usersQuery.refetch()}>
                  <Search className="size-4" />
                  {t('adminConsole.users.search')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
                <div className="max-h-[640px] overflow-auto rounded-lg border">
                  {usersQuery.isLoading ? <LoadingBlock className="h-64" /> : users.length ? users.map((user) => (
                    <button
                      key={user.user_id}
                      type="button"
                      className={`flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/60 ${selectedUserId === user.user_id ? 'bg-muted' : ''}`}
                      onClick={() => setSelectedUserId(user.user_id)}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{user.display_name || user.username}</span>
                        <span className="block truncate nf-mono text-xs text-muted-foreground">{user.user_id}</span>
                      </span>
                      {user.is_admin ? <Badge variant="secondary">{t('adminConsole.users.yes')}</Badge> : null}
                    </button>
                  )) : <EmptyState label={t('adminConsole.users.empty')} />}
                </div>

                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <KpiCard icon={WalletCards} label={t('adminConsole.billing.balance.available')} value={formatCredits(balanceQuery.data?.available)} />
                    <KpiCard icon={WalletCards} label={t('adminConsole.billing.balance.reserved')} value={formatCredits(balanceQuery.data?.reserved)} />
                    <KpiCard icon={Coins} label={t('adminConsole.billing.metrics.credits')} value={formatCredits(selectedUserTotal.credits)} />
                    <KpiCard icon={TrendingUp} label={t('adminConsole.billing.metrics.margin')} value={formatCurrency(selectedUserTotal.margin_usd)} detail={formatPercent(marginRate(selectedUserTotal), 2)} />
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <Card className="rounded-lg">
                      <CardHeader><CardTitle>{t('adminConsole.billing.byService')}</CardTitle></CardHeader>
                      <CardContent>{selectedUserServiceQuery.isLoading ? <LoadingBlock /> : selectedUserServiceRows.length ? <ServiceShare rows={selectedUserServiceRows} t={t} /> : <EmptyState label={t('adminConsole.billing.selectUser')} />}</CardContent>
                    </Card>
                    <Card className="rounded-lg">
                      <CardHeader><CardTitle>{t('adminConsole.billing.byProject')}</CardTitle></CardHeader>
                      <CardContent>
                        {selectedUserUsageQuery.isLoading ? <LoadingBlock /> : projectRows.length ? (
                          <Table>
                            <TableHeader><TableRow><TableHead>{t('adminConsole.billing.columns.project')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.credits')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.cost')}</TableHead></TableRow></TableHeader>
                            <TableBody>{projectRows.map((row) => <TableRow key={row.project_id}><TableCell className="nf-mono">{row.project_id}</TableCell><TableCell className="nf-mono text-right">{formatCredits(row.credits)}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(row.cost_usd)}</TableCell></TableRow>)}</TableBody>
                          </Table>
                        ) : <EmptyState label={t('adminConsole.billing.selectUser')} />}
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="rounded-lg">
                    <CardHeader><CardTitle>{t('adminConsole.billing.byModel')}</CardTitle></CardHeader>
                    <CardContent>
                      {selectedUserModelQuery.isLoading ? <LoadingBlock /> : selectedUserModelRows.length ? (
                        <Table>
                          <TableHeader><TableRow><TableHead>{t('billing.usage.colModel')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.csv.quantity')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.credits')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.margin')}</TableHead></TableRow></TableHeader>
                          <TableBody>{selectedUserModelRows.map((row, index) => <TableRow key={`${row.model_alias}-${index}`}><TableCell>{row.model_alias || t('adminConsole.billing.unknown')}</TableCell><TableCell className="nf-mono text-right">{formatNumber(row.quantity)}</TableCell><TableCell className="nf-mono text-right">{formatCredits(row.credits)}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(row.margin_usd)}</TableCell></TableRow>)}</TableBody>
                        </Table>
                      ) : <EmptyState label={t('adminConsole.billing.selectUser')} />}
                    </CardContent>
                  </Card>

                  <Card className="rounded-lg">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>{t('adminConsole.billing.ledger')}</CardTitle>
                      <Button type="button" variant="outline" size="sm" onClick={exportLedgerRows} disabled={!ledgerRows.length}><Download className="size-4" />{t('adminConsole.billing.exportCsv')}</Button>
                    </CardHeader>
                    <CardContent>{ledgerQuery.isLoading ? <LoadingBlock /> : ledgerRows.length ? <LedgerTable rows={ledgerRows} selectedUserId={selectedUserId} t={t} /> : <EmptyState label={t('adminConsole.billing.selectUser')} />}</CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details" className="space-y-4">
          <Card className="rounded-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('adminConsole.billing.detailsTitle')}</CardTitle>
                <CardDescription>{t('adminConsole.billing.detailsDescription')}</CardDescription>
              </div>
              <Button type="button" variant="outline" onClick={exportUsageRows} disabled={!summaryRows.length}><Download className="size-4" />{t('adminConsole.billing.exportCsv')}</Button>
            </CardHeader>
            <CardContent>{summaryQuery.isLoading ? <LoadingBlock className="h-72" /> : summaryRows.length ? <UsageRowsTable rows={summaryRows} users={users} t={t} /> : <EmptyState label={t('adminConsole.billing.empty')} />}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="finance" className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="rounded-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('adminConsole.billing.finance.monthlyCost')}</CardTitle>
                  <CardDescription>{month}</CardDescription>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={exportUsageRows} disabled={!summaryRows.length}><Download className="size-4" />{t('adminConsole.billing.exportCsv')}</Button>
              </CardHeader>
              <CardContent>{byServiceQuery.isLoading ? <LoadingBlock /> : financeByServiceRows.length ? <FinanceServiceTable rows={financeByServiceRows} t={t} /> : <EmptyState label={t('adminConsole.billing.empty')} />}</CardContent>
            </Card>
            <Card className="rounded-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('adminConsole.billing.finance.dailyReport')}</CardTitle>
                  <CardDescription>{t('adminConsole.billing.finance.dailyDescription')}</CardDescription>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={exportDailyRows} disabled={!dailyRows.length}><Download className="size-4" />{t('adminConsole.billing.exportCsv')}</Button>
              </CardHeader>
              <CardContent>{dailyQuery.isLoading ? <LoadingBlock /> : dailyRows.length ? <DailyFinanceTable rows={dailyRows} t={t} /> : <EmptyState label={t('adminConsole.billing.empty')} />}</CardContent>
            </Card>
          </div>
          <Card className="rounded-lg">
            <CardHeader><CardTitle>{t('adminConsole.billing.finance.ledgerReport')}</CardTitle><CardDescription>{t('adminConsole.billing.finance.ledgerDescription')}</CardDescription></CardHeader>
            <CardContent>{ledgerRows.length ? <LedgerTable rows={ledgerRows} selectedUserId={selectedUserId} t={t} /> : <EmptyState label={t('adminConsole.billing.selectUser')} />}</CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="wallet" className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-2">
            <Card className="rounded-lg">
              <CardHeader><CardTitle>{t('adminConsole.billing.wallet.topUpTitle')}</CardTitle><CardDescription>{t('adminConsole.billing.wallet.topUpDescription')}</CardDescription></CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); if (!selectedUserId.trim()) toast.error(t('adminConsole.billing.wallet.userRequired')); else if (asFiniteNumber(topUpAmount) <= 0) toast.error(t('adminConsole.billing.wallet.amountInvalid')); else topUpMutation.mutate() }}>
                  <div className="space-y-2"><Label htmlFor="billing-topup-user">{t('adminConsole.billing.filters.user')}</Label><Input id="billing-topup-user" value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)} placeholder={t('adminConsole.billing.filters.userPlaceholder')} /></div>
                  <div className="space-y-2"><Label htmlFor="billing-topup-amount">{t('adminConsole.billing.wallet.amount')}</Label><Input id="billing-topup-amount" value={topUpAmount} onChange={(event) => setTopUpAmount(event.target.value)} type="number" min="1" step="1" /></div>
                  <div className="space-y-2"><Label htmlFor="billing-topup-note">{t('adminConsole.billing.wallet.note')}</Label><Input id="billing-topup-note" value={topUpNote} onChange={(event) => setTopUpNote(event.target.value)} placeholder={t('adminConsole.billing.wallet.notePlaceholder')} /></div>
                  <Button type="submit" disabled={topUpMutation.isPending}>{topUpMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Coins className="size-4" />}{t('adminConsole.billing.wallet.topUp')}</Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-lg">
              <CardHeader><CardTitle>{t('adminConsole.billing.wallet.codesTitle')}</CardTitle><CardDescription>{t('adminConsole.billing.wallet.codesDescription')}</CardDescription></CardHeader>
              <CardContent>
                <form className="grid gap-4 sm:grid-cols-2" onSubmit={(event) => { event.preventDefault(); if (asFiniteNumber(codeCount) <= 0 || asFiniteNumber(codeAmount) <= 0) toast.error(t('adminConsole.billing.wallet.amountInvalid')); else codesMutation.mutate() }}>
                  <div className="space-y-2"><Label htmlFor="billing-code-count">{t('adminConsole.billing.wallet.count')}</Label><Input id="billing-code-count" value={codeCount} onChange={(event) => setCodeCount(event.target.value)} type="number" min="1" step="1" /></div>
                  <div className="space-y-2"><Label htmlFor="billing-code-amount">{t('adminConsole.billing.wallet.amount')}</Label><Input id="billing-code-amount" value={codeAmount} onChange={(event) => setCodeAmount(event.target.value)} type="number" min="1" step="1" /></div>
                  <div className="space-y-2">
                    <Label>{t('adminConsole.billing.wallet.purpose')}</Label>
                    <Select value={codePurpose} onValueChange={(value) => setCodePurpose(value as RedemptionCodePurpose)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CODE_PURPOSES.map((purpose) => <SelectItem key={purpose} value={purpose}>{t(`adminConsole.billing.wallet.purposes.${purpose}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label htmlFor="billing-code-expire">{t('adminConsole.billing.wallet.expiresAt')}</Label><Input id="billing-code-expire" value={codeExpiresAt} onChange={(event) => setCodeExpiresAt(event.target.value)} type="date" /></div>
                  <div className="space-y-2"><Label htmlFor="billing-code-note">{t('adminConsole.billing.wallet.note')}</Label><Input id="billing-code-note" value={codeNote} onChange={(event) => setCodeNote(event.target.value)} placeholder={t('adminConsole.billing.wallet.notePlaceholder')} /></div>
                  <div className="sm:col-span-2"><Button type="submit" disabled={codesMutation.isPending}>{codesMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <ReceiptText className="size-4" />}{t('adminConsole.billing.wallet.generateCodes')}</Button></div>
                </form>
                {generatedCodes.length ? (
                  <div className="mt-4 space-y-2">
                    {generatedCodes.map((item) => (
                      <div key={item.code} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                        <span className="nf-mono text-sm">{item.code}</span>
                        <Button type="button" variant="outline" size="sm" onClick={() => copyText(item.code, t('adminConsole.billing.wallet.copySuccess'))}><Clipboard className="size-4" />{t('adminConsole.billing.wallet.copy')}</Button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function UsageRowsTable({ rows, users, t }: { rows: AdminUsageRow[]; users: AdminUser[]; t: (key: string) => string }) {
  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('adminConsole.billing.columns.user')}</TableHead>
            <TableHead>{t('adminConsole.billing.columns.project')}</TableHead>
            <TableHead>{t('adminConsole.billing.columns.service')}</TableHead>
            <TableHead>{t('billing.usage.colModel')}</TableHead>
            <TableHead className="text-right">{t('adminConsole.billing.csv.quantity')}</TableHead>
            <TableHead className="text-right">{t('adminConsole.billing.metrics.cost')}</TableHead>
            <TableHead className="text-right">{t('adminConsole.billing.metrics.credits')}</TableHead>
            <TableHead className="text-right">{t('adminConsole.billing.metrics.margin')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.user_id}-${row.project_id}-${row.service_kind}-${row.model_alias}-${index}`}>
              <TableCell><div>{userName(users, row.user_id)}</div><div className="nf-mono text-xs text-muted-foreground">{row.user_id}</div></TableCell>
              <TableCell className="nf-mono text-xs">{row.project_id}</TableCell>
              <TableCell>
                <div>{row.service_kind ? t(serviceLabelKey(row.service_kind)) : ''}</div>
                <TokenSplitDetails row={row} t={t} />
              </TableCell>
              <TableCell>{row.model_alias}</TableCell>
              <TableCell className="nf-mono text-right">{formatNumber(row.quantity)}</TableCell>
              <TableCell className="nf-mono text-right">{formatCurrency(row.cost_usd)}</TableCell>
              <TableCell className="nf-mono text-right">{formatCredits(row.credits)}</TableCell>
              <TableCell className="nf-mono text-right">{formatCurrency(row.margin_usd)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function FinanceServiceTable({ rows, t }: { rows: Array<AdminUsageRow & { revenue_usd: number; margin_rate: number }>; t: (key: string) => string }) {
  return (
    <Table>
      <TableHeader><TableRow><TableHead>{t('adminConsole.billing.columns.service')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.csv.quantity')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.cost')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.revenue')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.marginRate')}</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((row) => <TableRow key={row.service_kind || row.model_alias}><TableCell>{row.service_kind ? t(serviceLabelKey(row.service_kind)) : t('adminConsole.billing.unknown')}</TableCell><TableCell className="nf-mono text-right">{formatNumber(row.quantity)}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(row.cost_usd)}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(row.revenue_usd)}</TableCell><TableCell className="nf-mono text-right">{formatPercent(row.margin_rate, 2)}</TableCell></TableRow>)}</TableBody>
    </Table>
  )
}

function DailyFinanceTable({ rows, t }: { rows: AdminUsageDailyRow[]; t: (key: string) => string }) {
  return (
    <Table>
      <TableHeader><TableRow><TableHead>{t('adminConsole.billing.csv.date')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.cost')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.revenue')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.margin')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.metrics.marginRate')}</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map((row) => <TableRow key={row.date}><TableCell>{row.date}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(row.cost_usd)}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(revenueUSD(row.credits))}</TableCell><TableCell className="nf-mono text-right">{formatCurrency(row.margin_usd)}</TableCell><TableCell className="nf-mono text-right">{formatPercent(marginRate(row), 2)}</TableCell></TableRow>)}</TableBody>
    </Table>
  )
}

function LedgerTable({ rows, selectedUserId, t }: { rows: Array<Record<string, unknown> & { type: string; amount: number; balance_after: number; created_at: number }>; selectedUserId: string; t: (key: string) => string }) {
  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader><TableRow><TableHead>{t('adminConsole.billing.csv.time')}</TableHead><TableHead>{t('adminConsole.billing.csv.userId')}</TableHead><TableHead>{t('adminConsole.billing.csv.type')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.csv.creditDelta')}</TableHead><TableHead className="text-right">{t('adminConsole.billing.csv.balanceAfter')}</TableHead><TableHead>{t('adminConsole.billing.csv.note')}</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((row, index) => <TableRow key={`${row.id ?? index}`}><TableCell>{formatDateTime(row.created_at)}</TableCell><TableCell className="nf-mono text-xs">{selectedUserId}</TableCell><TableCell>{formatLedgerType(row.type, t)}</TableCell><TableCell className="nf-mono text-right">{formatCredits(row.amount)}</TableCell><TableCell className="nf-mono text-right">{formatCredits(row.balance_after)}</TableCell><TableCell>{String(row.note ?? row.ref_job_id ?? '')}</TableCell></TableRow>)}</TableBody>
      </Table>
    </div>
  )
}
